/**
 * @module agent/adapters/services/MessageManager
 *
 * Component-based message management with encapsulated state.
 * Follows ARCHITECTURE.md design: Components own their domain state.
 *
 * Phase 4 Acceptance Criteria:
 * - AC2: MessageManager class owns `messages` Map state
 * - AC3: Receives dependencies via constructor (DI pattern)
 * - AC5: Independently testable with mocked dependencies
 *
 * @example
 * import MessageManager from './services/MessageManager';
 * import * as storageUtils from './adapterStorageUtils';
 *
 * const manager = new MessageManager(store, {
 *   loadStoredMessages: storageUtils.loadStoredMessages,
 *   saveStoredMessages: storageUtils.saveStoredMessages
 * });
 * manager.load();
 * manager.add('session-1', { id: 'msg-1', content: 'Hello' });
 */

const ADAPTER_NAME = 'AGENTCORE';

/**
 * Message manager with encapsulated state and dependency injection.
 */
class MessageManager {
  /**
   * @param {Object} store - MobX store with authStore
   * @param {Object} storageUtils - Storage utility functions (DI)
   * @param {Function} storageUtils.loadStoredMessages - Load messages from localStorage
   * @param {Function} storageUtils.saveStoredMessages - Save messages to localStorage
   */
  constructor(store, storageUtils) {
    this._store = store;
    this._storageUtils = storageUtils;

    // Encapsulated state - Map<sessionId, Message[]>
    this._messages = new Map();

    // Change callbacks
    this._changeCallbacks = [];
  }

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  /**
   * Load messages from storage.
   * @param {Function} [transformer] - Optional transformer for loaded messages
   */
  load(transformer) {
    const defaultTransformer = (msg) => ({
      ...msg,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
    });

    const messages = this._storageUtils.loadStoredMessages(
      this._store,
      ADAPTER_NAME,
      transformer || defaultTransformer
    );

    this._messages = messages;
  }

  /**
   * Save messages to storage.
   */
  save() {
    this._storageUtils.saveStoredMessages(this._messages, this._store, ADAPTER_NAME);
  }

  // ============================================================================
  // CRUD OPERATIONS
  // ============================================================================

  /**
   * Add a message to a session.
   *
   * @param {string} sessionId - Session ID
   * @param {Object} message - Message to add
   * @returns {Object} Added message with timestamp
   */
  add(sessionId, message) {
    if (!this._messages.has(sessionId)) {
      this._messages.set(sessionId, []);
    }

    const messageWithTimestamp = {
      ...message,
      timestamp: message.timestamp || new Date()
    };

    this._messages.get(sessionId).push(messageWithTimestamp);
    this.save();
    this._notifyChange(sessionId);

    return messageWithTimestamp;
  }

  /**
   * Update a message in a session.
   *
   * @param {string} sessionId - Session ID
   * @param {string} messageId - Message ID
   * @param {Object} updates - Properties to update
   * @returns {boolean} True if updated, false if not found
   */
  update(sessionId, messageId, updates) {
    const messages = this._messages.get(sessionId);

    if (!messages) {
      return false;
    }

    const index = messages.findIndex((m) => m.id === messageId);

    if (index === -1) {
      return false;
    }

    messages[index] = {
      ...messages[index],
      ...updates,
      id: messages[index].id // Prevent ID change
    };

    this.save();
    this._notifyChange(sessionId);
    return true;
  }

  /**
   * Get all messages for a session.
   * Returns a copy to prevent external modification.
   *
   * @param {string} sessionId - Session ID
   * @returns {Array} Array of messages (copy)
   */
  getForSession(sessionId) {
    const messages = this._messages.get(sessionId);
    return messages ? [...messages] : [];
  }

  /**
   * Set all messages for a session.
   *
   * @param {string} sessionId - Session ID
   * @param {Array} messages - Messages array
   */
  setForSession(sessionId, messages) {
    this._messages.set(sessionId, [...messages]);
    this.save();
    this._notifyChange(sessionId);
  }

  /**
   * Delete all messages for a session.
   *
   * @param {string} sessionId - Session ID
   */
  deleteForSession(sessionId) {
    this._messages.delete(sessionId);
    this.save();
    this._notifyChange(sessionId);
  }

  /**
   * Check if session has messages.
   *
   * @param {string} sessionId - Session ID
   * @returns {boolean} True if session exists
   */
  hasSession(sessionId) {
    return this._messages.has(sessionId);
  }

  /**
   * Get number of sessions with messages.
   *
   * @returns {number} Session count
   */
  countSessions() {
    return this._messages.size;
  }

  /**
   * Migrate messages to a new session ID.
   * Used when backend returns different ID than client-generated.
   *
   * @param {string} oldSessionId - Current session ID
   * @param {string} newSessionId - New session ID
   */
  updateSessionId(oldSessionId, newSessionId) {
    const messages = this._messages.get(oldSessionId);

    if (messages) {
      // Update sessionId in each message
      const migrated = messages.map((msg) => ({
        ...msg,
        sessionId: newSessionId
      }));

      this._messages.delete(oldSessionId);
      this._messages.set(newSessionId, migrated);
      this.save();
      this._notifyChange(newSessionId);
    }
  }

  /**
   * Set all messages at once.
   * Used by adapter for bulk operations.
   *
   * @param {Map} messages - Messages Map to set
   */
  setAll(messages) {
    this._messages = new Map(messages);
    this.save();
  }

  /**
   * Clear all messages.
   */
  clear() {
    this._messages.clear();
    this.save();
  }

  /**
   * Get the internal messages Map.
   *
   * @internal For test inspection only. Production code should use manager
   * methods (getForSession, add, setForSession, entries, etc.).
   * Returns direct reference - modifications bypass validation/persistence.
   *
   * @returns {Map} Messages Map reference
   */
  getMap() {
    return this._messages;
  }

  // ============================================================================
  // DIRECT MAP OPERATIONS (Phase 4.7 - for adapter migration)
  // ============================================================================

  /**
   * Get all session IDs that have messages.
   * Returns a copy array (safe to modify).
   *
   * @returns {Array<string>} Array of session IDs
   */
  sessionIds() {
    return Array.from(this._messages.keys());
  }

  /**
   * Get iterator over [sessionId, messages] pairs.
   * Enables for...of iteration over all sessions with messages.
   *
   * @returns {Iterator} Iterator over [sessionId, messages[]] entries
   */
  entries() {
    return this._messages.entries();
  }

  // ============================================================================
  // CALLBACKS
  // ============================================================================

  /**
   * Register callback for message changes.
   *
   * @param {Function} callback - Called with (sessionId, messages) when messages change
   */
  onChange(callback) {
    this._changeCallbacks.push(callback);
  }

  /**
   * Notify all registered callbacks of a change.
   * @private
   */
  _notifyChange(sessionId) {
    const messages = this.getForSession(sessionId);
    this._changeCallbacks.forEach((cb) => {
      try {
        cb(sessionId, messages);
      } catch (error) {
        console.error('[MessageManager] Callback error:', error);
      }
    });
  }
}

export default MessageManager;
